/* ShowerSMART — cart checks strip.
 *
 * Walks three steps: stock checked -> everything in stock -> free shipping.
 * Markup and every resolved state come from snippets/showersmart-cart-checks.liquid,
 * server-rendered from the real cart. This file only decides WHEN each node
 * lights up. It never decides what a node says.
 *
 * WHY A CUSTOM ELEMENT AND NOT AN INLINE SCRIPT
 * Two things break the obvious version, and both are invisible until tested:
 *
 *   1. CartDrawer.renderContents() replaces the drawer's innerHTML after every
 *      cart change. Scripts injected via innerHTML never execute, so an inline
 *      script would run once on page load and never again. Custom elements
 *      upgrade automatically when inserted that way, so this keeps working.
 *
 *   2. The drawer is in the DOM but hidden on page load. An inline script would
 *      animate to completion behind a closed drawer and the shopper would only
 *      ever see the end state.
 *
 * So inside a drawer we wait for it to open. DrawerFixed.open()
 * (assets/critical-global.js:390) adds `active` to the <cart-drawer> element on
 * BOTH paths -- header icon click and post-add-to-cart -- so watching that one
 * class covers everything. No theme JavaScript is patched.
 */
(function () {
  if (customElements.get('showersmart-cart-checks')) return;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

  customElements.define('showersmart-cart-checks', class extends HTMLElement {
    connectedCallback() {
      this.strip = this.querySelector('[data-sscp]');
      if (!this.strip) return;

      this.statusEl = this.querySelector('[data-sscp-status]');
      this.nodes = Array.prototype.slice.call(this.querySelectorAll('.sscp__node'));
      if (!this.nodes.length) return;

      this.finalPhase = this.num(this.strip.dataset.finalPhase, 1, 3, 3);
      this.step1Ms = this.num(this.strip.dataset.step1Ms, 0, 4000, 2000);
      this.stepMs = this.num(this.strip.dataset.stepMs, 0, 3000, 1000);

      // Reduced motion: the markup already IS the resolved state, so doing
      // nothing is the correct outcome. There is no information in the wait.
      if (REDUCED && REDUCED.matches) return;
      if (this.step1Ms === 0 && this.stepMs === 0) return;

      var drawer = this.closest('cart-drawer');
      if (!drawer) { this.run(); return; }   // cart page: nothing to wait for

      // Replay on every open, and reset when it closes so the next open starts
      // from the beginning rather than showing a finished bar.
      this.observer = new MutationObserver(function () {
        if (drawer.classList.contains('active')) this.run();
        else this.reset();
      }.bind(this));
      this.observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });

      // The drawer may already be open when we upgrade -- that is exactly the
      // add-to-cart path, where renderContents() re-inserts us into a drawer
      // that is on its way to open.
      if (drawer.classList.contains('active')) this.run();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.clear();
    }

    num(raw, min, max, fallback) {
      var n = parseInt(raw, 10);
      return (n >= min && n <= max) ? n : fallback;
    }

    clear() {
      if (this.timer) { window.clearTimeout(this.timer); this.timer = null; }
    }

    reset() {
      this.clear();
      this.running = false;
      this.strip.removeAttribute('data-anim');
      this.strip.removeAttribute('data-phase');
      this.nodes.forEach(function (n) { n.classList.remove('is-on', 'is-busy'); });
    }

    run() {
      if (this.running) return;
      this.running = true;
      this.strip.dataset.anim = 'on';
      this.show(1);
      this.timer = window.setTimeout(this.advance.bind(this, 2), this.step1Ms);
    }

    advance(phase) {
      if (phase > this.finalPhase) {
        // Hand control back to the server-rendered resting state.
        this.strip.removeAttribute('data-anim');
        this.strip.removeAttribute('data-phase');
        this.nodes.forEach(function (n) { n.classList.remove('is-busy'); });
        return;
      }
      this.show(phase);
      this.timer = window.setTimeout(this.advance.bind(this, phase + 1), this.stepMs);
    }

    show(phase) {
      // Fill stops at the last node the run is allowed to reach: a bar filled to
      // 100% over an out-of-stock warning would read as "all clear".
      var fill = phase === 1 ? '16%'
               : phase === 2 ? (this.finalPhase >= 3 ? '50%' : '100%')
               : '100%';

      this.strip.dataset.phase = String(phase);
      this.strip.style.setProperty('--sscp-fill', fill);

      this.nodes.forEach(function (n) {
        var i = parseInt(n.dataset.node, 10);
        n.classList.toggle('is-on', i <= phase);
        n.classList.toggle('is-busy', i === 1 && phase === 1);   // only while checking
      });

      if (this.statusEl) {
        var active = this.nodes[Math.min(phase, this.nodes.length) - 1];
        if (active) {
          var busy = (phase === 1 && active.dataset.statusBusy) || '';
          this.statusEl.textContent = busy || active.dataset.status || '';
        }
      }
    }
  });
})();
